import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useModal } from '../../../components/Modal/useModal';
import { DEFAULT_ERROR } from '../../../components/Notifications/Notification';
import { postAgendaConfirm } from '../../../core/api';
import { addNotification } from '../../../core/store/notificationsSlice';
import { SellerFilingReportToConfirm } from '../../../core/types';
import { useAppDispatch, useAppSelector } from '../../../hooks';

export default function useAgendaModal() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector((s) => s.user.user);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);
  const { t } = useTranslation();
  const [isConfirmingAgenda, setIsConfirmingAgenda] = useState(false);
  const [reportToConfirmAgenda, setSellerFilingReportToConfirmAgenda] =
    useState<SellerFilingReportToConfirm | undefined>(undefined);

  const {
    isModalOpened: isAgendaModalOpened,
    closeModal: closeAgendaModal,
    openModal: openAgendaModal,
  } = useModal();

  const onClickConfirmAgenda =
    (report: SellerFilingReportToConfirm | undefined) => () => {
      if (!report) return;
      setIsConfirmingAgenda(true);
      postAgendaConfirm(report.id, activeCompany?.company_id)
        .then(() => {
          setIsConfirmingAgenda(false);
          queryClient.setQueryData(
            report.completed_filing
              ? ['SellerCompletedFiling', user!.Id, activeCompany?.company_id]
              : ['SellerOngoingFiling', user!.Id, activeCompany?.company_id],
            (oldReports: SellerFilingReportToConfirm[] | undefined) => {
              return (oldReports || []).map((r) => {
                if (r.id === report.id) {
                  return {
                    ...r,
                    agenda_confirmed: true,
                  };
                }

                return r;
              });
            }
          );
          dispatch(
            addNotification({
              text: t('Report submission confirmed'),
              type: 'success',
            })
          );
          closeAgendaModal();
        })
        .catch(() => {
          setIsConfirmingAgenda(false);
          dispatch(
            addNotification({
              text: t(DEFAULT_ERROR),
              type: 'error',
            })
          );
        });
    };

  const handleOpenAgendaModal = (report: SellerFilingReportToConfirm) => () => {
    setSellerFilingReportToConfirmAgenda(report);
    openAgendaModal();
  };

  return {
    isAgendaModalOpened,
    closeAgendaModal,
    handleOpenAgendaModal,
    onClickConfirmAgenda,
    isConfirmingAgenda,
    reportToConfirmAgenda,
  };
}
